"use client";

import { useEffect, useState } from "react";
import { formatMoney, formatPercent } from "@/lib/format";
import type { StartupRecord } from "@/lib/types";

const PAGE_SIZE = 50;

type Props = {
  startups: StartupRecord[];
  onSelectStartup?: (startup: StartupRecord) => void;
  selectedStartupSlug?: string | null;
};

function growthClass(value: number | null): string {
  if (value === null) return "growth-neutral";
  return value > 0 ? "growth-positive" : value < 0 ? "growth-negative" : "growth-neutral";
}

export function StartupTable({ startups, onSelectStartup, selectedStartupSlug }: Props) {
  const [page, setPage] = useState(1);
  const sorted = [...startups].sort((a, b) => b.mrr - a.mrr);

  useEffect(() => {
    setPage(1);
  }, [startups]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageData = sorted.slice(start, start + PAGE_SIZE);

  if (!sorted.length) {
    return (
      <div className="card">
        <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 24 }}>
          No startups match the current filters.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="table-wrapper">
        <table className="niche-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Startup</th>
              <th>MRR</th>
              <th>Revenue (30d)</th>
              <th>Growth</th>
              <th>Audience</th>
              <th>Provider</th>
              <th>For Sale</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((startup, index) => {
              const isSelected = selectedStartupSlug === startup.slug;

              return (
                <tr
                  key={`${startup.slug}-${index}`}
                  className={isSelected ? "startup-table-row startup-table-row--selected" : "startup-table-row"}
                >
                  <td className="cell-num">{start + index + 1}</td>
                  <td className="cell-name">
                    <div className="startup-cell startup-cell--stack">
                      <button
                        type="button"
                        className="startup-link startup-link--button"
                        onClick={() => onSelectStartup?.(startup)}
                      >
                        {startup.icon && (
                          <img
                            src={startup.icon}
                            alt=""
                            className="startup-logo"
                            width={24}
                            height={24}
                          />
                        )}
                        <span>{startup.name}</span>
                      </button>
                      <a
                        href={startup.trustmrr_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="startup-secondary-link"
                      >
                        View on TrustMRR
                      </a>
                    </div>
                  </td>
                  <td className="cell-money">{formatMoney(startup.mrr)}</td>
                  <td className="cell-money">{formatMoney(startup.revenue_30d)}</td>
                  <td>
                    <span className={growthClass(startup.growth_30d)}>
                      {formatPercent(startup.growth_30d)}
                    </span>
                  </td>
                  <td className="cell-num">{startup.target_audience ?? "-"}</td>
                  <td className="cell-num">{startup.payment_provider ?? "-"}</td>
                  <td>
                    {startup.on_sale && startup.asking_price !== null ? (
                      <span className="cell-badge cell-badge--warning">
                        {formatMoney(startup.asking_price)}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="table-pagination">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page <= 1}
          >
            Prev
          </button>
          <span className="pagination-info">
            {start + 1}-{Math.min(start + PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
